import React from "react";
import { Form } from "react-final-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { Alert, Box } from "@mui/material";
import { FORM_ERROR } from "final-form";

import { LoadingButton } from "ui";

import { selectUserId, useCreateAccountMutation, useGetUserMeAccountsQuery } from "fond/api";
import { TextField } from "fond/form/fields";
import { CreateAccountRequestBody } from "fond/types";
import { setAccountContext } from "fond/utils/accountContextStorage";
import { compose, required } from "fond/utils/validation";

interface IFormData {
  accountName: string;
}

const CreateAccountForm: React.FC = () => {
  const navigate = useNavigate();
  const [createAccount] = useCreateAccountMutation();
  const userId = useSelector(selectUserId) ?? "";
  // refetch user linked accounts on create for admin/allocation checks
  const { refetch } = useGetUserMeAccountsQuery();

  const handleOnSubmit = async ({ accountName }: IFormData) => {
    const accountDetails: CreateAccountRequestBody = {
      Name: accountName,
      BusinessName: null,
      BusinessType: null,
      BusinessDescription: null,
      Email: null,
      Phone: null,
      AddressLine1: null,
      AddressLine2: null,
      City: null,
      State: null,
      PostalCode: null,
      Country: null,
      SystemOfMeasurement: "imperial",
      Currency: "USD",
      BomArchRestrictions: null,
      BackgroundColor: null,
      ForegroundColor: null,
      Logo: null,
      AdminUserID: userId,
    };

    try {
      const response = await createAccount(accountDetails).unwrap();
      refetch();
      setAccountContext(userId, response.ID);
      navigate("/");
    } catch (error) {
      return { [FORM_ERROR]: "Failed to create account." };
    }
    return {};
  };

  return (
    <Form<IFormData>
      initialValues={{
        accountName: "",
      }}
      onSubmit={handleOnSubmit}
      render={({ handleSubmit, submitting, submitError }) => (
        <form onSubmit={handleSubmit} data-testid="create-account-form">
          <Box>
            <Box mb={3}>
              <TextField name="accountName" label="What's your account name?" validate={compose(required)} />
            </Box>
            {submitError && (
              <Box mb={2}>
                <Alert data-testid="create-account-error-message" severity="error">
                  {submitError}
                </Alert>
              </Box>
            )}
            <Box>
              <LoadingButton loading={submitting} data-testid="create-account-button" type="submit" fullWidth variant="contained" color="primary">
                CREATE ACCOUNT
              </LoadingButton>
            </Box>
          </Box>
        </form>
      )}
    />
  );
};

export default CreateAccountForm;
